import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack as MuiStack,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatMinor, slugify, type Category, type Product } from '@eprintsmart/shared';
import { createProduct, subscribeToCategories, subscribeToProducts } from '../services/catalog';
import { SubmitAllToIndexNow } from '../components/SubmitAllToIndexNow';
import { useNavigate } from 'react-router-dom';

export function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [categories, setCategories] = useState<Category[]>([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => subscribeToProducts((next) => {
    setProducts(next);
    setLoading(false);
  }), []);

  useEffect(() => subscribeToCategories(setCategories), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (status !== 'ALL' && p.status !== status) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q);
    });
  }, [products, search, status]);

  const missingImages = products.filter((p) => !p.imageIds?.length).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h1" sx={{ flexGrow: 1 }}>
          Products
        </Typography>
        <TextField
          size="small"
          placeholder="Search name or slug"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240 }}
        />
        <TextField
          size="small"
          select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ width: 150 }}
        >
          <MenuItem value="ALL">All statuses</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="ARCHIVED">Archived</MenuItem>
        </TextField>
        <SubmitAllToIndexNow />
        <Button variant="contained" onClick={() => setCreating(true)}>New product</Button>
      </Stack>

      <NewProductDialog
        open={creating}
        categories={categories}
        onClose={() => setCreating(false)}
      />

      {missingImages > 0 && (
        <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" fontSize="small" />
          <Typography variant="body2">
            {missingImages} product{missingImages === 1 ? ' has' : 's have'} no images yet — they will
            render as blank tiles on the storefront.
          </Typography>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Trim size</TableCell>
                <TableCell align="right">From</TableCell>
                <TableCell align="right">Min qty</TableCell>
                <TableCell align="right">GST</TableCell>
                <TableCell align="center">Images</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/catalog/products/${p.id}`} underline="hover">
                      {p.name}
                    </Link>
                    <Typography variant="caption" color="text.secondary" display="block">
                      /{p.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {p.printSpec
                      ? `${p.printSpec.trimWidthMm} × ${p.printSpec.trimHeightMm} mm`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">{formatMinor(p.basePriceMinor)}</TableCell>
                  <TableCell align="right">{p.minQty}</TableCell>
                  <TableCell align="right">{p.gstRatePercent}%</TableCell>
                  <TableCell align="center">
                    {p.imageIds?.length ? (
                      p.imageIds.length
                    ) : (
                      <Chip size="small" label="none" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status}
                      variant="outlined"
                      color={p.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No products match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}


function NewProductDialog({
  open,
  categories,
  onClose,
}: {
  open: boolean;
  categories: Category[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const assignable = categories.filter((c) => c.level > 0).sort((a, b) => a.name.localeCompare(b.name));

  const create = async () => {
    setError(null);
    if (name.trim().length < 2) return setError('Enter a product name.');
    if (!categoryId) return setError('Choose a category, or nobody will find it.');

    setSaving(true);
    try {
      const id = await createProduct({
        slug: slug || slugify(name),
        name: name.trim(),
        categoryIds: [categoryId],
      });
      onClose();
      // Straight into the editor — a bare product still needs a spec and a
      // price ladder before it can be sold.
      navigate(`/catalog/products/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the product.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New product</DialogTitle>
      <DialogContent>
        <MuiStack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Name" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setSlug(slugify(e.target.value)); }}
          />
          <TextField
            label="Slug" value={slug}
            onChange={(e) => setSlug(slugify(e.target.value))}
            helperText="Becomes the page URL, and cannot be changed later."
          />
          <TextField
            label="Category" select value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {assignable.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <Alert severity="info">
            It is created as a draft with a default visiting-card print spec. Set the real
            dimensions and a price ladder before making it active.
          </Alert>
        </MuiStack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={saving}>Create and edit</Button>
      </DialogActions>
    </Dialog>
  );
}
